const IncomesPage = lazy(() => import('./pages/IncomesPage'));
const BookingSettingsPage = lazy(() => import('./pages/BookingSettingsPage'));
import React, { lazy, Suspense, useEffect } from 'react';
import ErrorNotificationContainer from './components/ErrorNotificationContainer';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, GlobalStyles, Box, CircularProgress, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lightTheme } from './theme/theme';
import { useAuthStore } from './store/authStore';
import { useProfilePictureStore } from './store/profilePictureStore';
import HandoverDelayChecker from './services/handoverDelayChecker';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ProfileGuard from './components/ProfileGuard';
import SubscriptionGuard from './components/SubscriptionGuard';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import NavigationRegistrar from './components/NavigationRegistrar';

// Pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const EmployeeLoginPage = lazy(() => import('./pages/EmployeeLoginPage'));
const DemoLoginPage = lazy(() => import('./pages/DemoLoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const ProfileSettingsPage = lazy(() => import('./pages/ProfileSettingsPage'));
const PublicAgencyProfile = lazy(() => import('./pages/PublicAgencyProfile'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const BasicInfoForm = lazy(() => import('./pages/BasicInfoForm'));
const VehiclesPage = lazy(() => import('./pages/VehiclesPage'));
const VehicleSalesPage = lazy(() => import('./pages/VehicleSalesPage'));
const MaintenanceVehiclesPage = lazy(() => import('./pages/MaintenanceVehiclesPage'));
const AddVehiclePage = lazy(() => import('./pages/AddVehiclePage'));
const EditVehiclePage = lazy(() => import('./pages/EditVehiclePage'));
const VehicleDetailsPage = lazy(() => import('./pages/VehicleDetailsPage'));
const VehicleDocumentsPage = lazy(() => import('./pages/VehicleDocumentsPage'));
const VehicleFleetDocumentsPage = lazy(() => import('./pages/VehicleFleetDocumentsPage'));
const VehicleSettingsPage = lazy(() => import('./pages/VehicleSettingsPage'));
const POSDashboard = lazy(() => import('./pages/POSDashboard'));
const POSNewBooking = lazy(() => import('./pages/POSNewBooking'));
const POSVehicleHandover = lazy(() => import('./pages/POSVehicleHandover'));
const POSVehicleReturn = lazy(() => import('./pages/POSVehicleReturn'));
const BookingsPage = lazy(() => import('./pages/BookingsPage'));
const QuotationsPage = lazy(() => import('./pages/QuotationsPage'));
const BookingDetailsPage = lazy(() => import('./pages/BookingDetailsPage'));
const BookingEvidencePage = lazy(() => import('./pages/BookingEvidencePage'));
const ErrorNotificationDemo = lazy(() => import('./components/ErrorNotificationDemo'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage2'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const RevenuePage = lazy(() => import('./pages/RevenuePage'));
const LeaseRevenuePage = lazy(() => import('./pages/LeaseRevenuePage'));
const BankTransactionsPage = lazy(() => import('./pages/BankTransactionsPage'));
const FischeCreditPage = lazy(() => import('./pages/FischeCreditPage'));
const CassiePage = lazy(() => import('./pages/CassiePage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientDetailsPage = lazy(() => import('./pages/ClientDetailsPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const RolesPage = lazy(() => import('./pages/RolesPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const PrivacyPage = lazy(() => import('./pages/legal/Privacy'));
const SecurityPage = lazy(() => import('./pages/legal/Security'));
const TermsPage = lazy(() => import('./pages/legal/Terms'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const ContractsPage = lazy(() => import('./pages/ContractsPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const RadarPage = lazy(() => import('./pages/RadarPage'));
const RadarDetailsPage = lazy(() => import('./pages/RadarDetailsPage'));

const PageLoader = () => (
  <Box display="flex" flexDirection="column" gap={1.5} alignItems="center" justifyContent="center" minHeight="320px">
    <CircularProgress size={28} />
    <Typography variant="body2" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

// Create a client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 10 * 1000, // 10 seconds - prevents excessive refetching
      gcTime: 30 * 1000, // 30 seconds - garbage collection time (was cacheTime)
      networkMode: 'always', // Prevents unwanted retries
    },
    mutations: {
      retry: 1,
      networkMode: 'always',
    },
  },
});

import employeeAuthService from './services/employeeAuthService';

const App: React.FC = () => {
  const { initializeAuth, isAuthenticated, isLoading } = useAuthStore();
  const { fetchProfilePicture } = useProfilePictureStore();

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Refresh role permissions after the owner changes access in another session.
  useEffect(() => {
    if (!isAuthenticated) return;
    let pending = false;
    const refresh = async () => {
      const session = useAuthStore.getState();
      if (pending || session.userType !== 'employee') return;
      pending = true;
      try {
        const updated = await employeeAuthService.refreshAgencyTimezone();
        const current = useAuthStore.getState();
        if (updated && current.userType === 'employee' && current.employee?.id === updated.id) current.setEmployee(updated);
      } finally { pending = false; }
    };
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => { window.removeEventListener('focus', refresh); window.clearInterval(timer); };
  }, [isAuthenticated]);

  // Fetch profile picture once when authenticated and initialization is complete
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      fetchProfilePicture();
    } else if (!isAuthenticated && !isLoading) {
      HandoverDelayChecker.stopMonitoring();
    }
  }, [isAuthenticated, isLoading, fetchProfilePicture]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            html: {
              width: '100%',
              height: '100%',
            },
            body: {
              width: '100%',
              minHeight: '100%',
              margin: 0,
              overflowX: 'hidden',
            },
            '#root': {
              width: '100%',
              minHeight: '100dvh',
            },
            '*': {
              boxSizing: 'border-box',
            },
            'img, video, canvas, svg': {
              maxWidth: '100%',
            },
          }}
        />
        <ErrorNotificationContainer />
        <Box sx={{ minHeight: '100dvh', width: '100%' }}>
          <Router>
            <NavigationRegistrar />
            <RouteErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes - Phone-based Authentication */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* Employee Login Route */}
              <Route path="/employee/login" element={<EmployeeLoginPage />} />

              {/* Demo Auto-Login - Only works when VITE_ENABLE_MOCK_AUTH=true */}
              <Route path="/demo" element={<DemoLoginPage />} />

              {/* Error Notification Demo */}
              <Route path="/error-demo" element={<ErrorNotificationDemo />} />

              {/* Public Agency Profile - No authentication required */}
              <Route path="/agency/:slug" element={<PublicAgencyProfile />} />
              <Route path="/agency/slug/:slug" element={<PublicAgencyProfile />} />

              {/* Basic Info Form - After phone verification */}
              <Route
                path="/basic-info"
                element={
                  <ProtectedRoute>
                    <BasicInfoForm />
                  </ProtectedRoute>
                }
              />

              {/* Onboarding - No Layout (dedicated onboarding flow) */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes with Layout */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <DashboardPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route path="/analytics" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <AnalyticsPage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/booking-settings" element={<ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout><BookingSettingsPage /></Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>} />
              <Route path="/incomes" element={<ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout><IncomesPage /></Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>} />
              <Route path="/expenses" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <ExpensesPage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/revenue" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <RevenuePage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/bank" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <BankTransactionsPage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/revenue/credit" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <FischeCreditPage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/revenue/cassie" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <CassiePage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/revenue/lease" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <LeaseRevenuePage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/calendar" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <CalendarPage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/radar" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <RadarPage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              <Route path="/radar/:id" element={
                <ProtectedRoute><ProfileGuard><SubscriptionGuard><Layout>
                  <RadarDetailsPage />
                </Layout></SubscriptionGuard></ProfileGuard></ProtectedRoute>
              } />

              {/* Subscription Routes */}
              <Route path="/pricing" element={<PricingPage />} />

              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <Layout>
                        <SubscriptionPage />
                      </Layout>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/employees"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <EmployeesPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clients"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <ClientsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/roles"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <RolesPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <TasksPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <Layout>
                        <SupportPage />
                      </Layout>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <SubscriptionGuard>
                      <Layout>
                        <ProfileSettingsPage />
                      </Layout>
                    </SubscriptionGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile/edit"
                element={<Navigate to="/profile" replace />}
              />

              <Route
                path="/vehicles"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <VehiclesPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/sales"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <VehicleSalesPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/maintenance-unavailable"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <MaintenanceVehiclesPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/settings"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <VehicleSettingsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/documents"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <VehicleFleetDocumentsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/add"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <AddVehiclePage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/:id/edit"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <EditVehiclePage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/:id"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <VehicleDetailsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/:id/documents"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <VehicleDocumentsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/clients/:phone"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <ClientDetailsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              {/* Bookings Routes */}
              <Route
                path="/quotations"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <QuotationsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <BookingsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <BookingsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/contracts"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <ContractsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/bookings/:id/evidence"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <Layout>
                        <BookingEvidencePage />
                      </Layout>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/bookings/:id"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <Layout>
                        <BookingDetailsPage />
                      </Layout>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/invoices"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <InvoicesPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              {/* POS Routes */}
              <Route
                path="/pos"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <POSDashboard />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pos/dashboard"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <POSDashboard />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pos/full-booking"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <POSNewBooking />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pos/handover"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <POSVehicleHandover />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pos/return"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <POSVehicleReturn />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pos/bookings"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <POSVehicleReturn />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pos/bookings/:id"
                element={
                  <ProtectedRoute>
                    <ProfileGuard>
                      <SubscriptionGuard>
                        <Layout>
                          <BookingDetailsPage />
                        </Layout>
                      </SubscriptionGuard>
                    </ProfileGuard>
                  </ProtectedRoute>
                }
              />

              {/* Legal Pages - Public routes */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/terms" element={<TermsPage />} />

              {/* Default Route */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 404 Route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            </RouteErrorBoundary>
          </Router>
        </Box>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
